import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import {
  namespaceApi,
  type ICreateOrganisationRequest,
  type INamespace,
} from '@/services/NamespaceApi';
import { describeLoadFailure, type ILoadFailure } from '@/services/loadFailure';
import { signOutRequirement } from '@/privacy/signOutChoice';
import { cacheSignOutRequired } from '@/privacy/signOutRequirementCache';

/** A namespace, plus the depth and children a tree render needs (F-0221). */
export interface INamespaceTreeRow {
  namespace: INamespace;
  depth: number;
  children: INamespaceTreeRow[];
}

/**
 * The namespace tree, and creating an organisation, a subgroup, or moving a work area into one
 * (F-0221). Mirrors `DecisionsStore`'s load/failure/loading shape.
 *
 * ⛔ Work-area **creation** inside a namespace stays on `ProjectStore.createProject` (REST,
 * `namespaceId`); this store is only what has no REST home — see the task file §2.1.
 */
export class NamespaceStore {
  /** `null` before the first read has answered. */
  tree: INamespace[] | null = null;
  failure: ILoadFailure | null = null;
  loading = false;

  private requestSerial = 0;

  constructor() {
    makeObservable(this, {
      tree: observable.ref,
      failure: observable.ref,
      loading: observable,
      organisations: computed,
      rows: computed,
      load: action,
      createOrganisation: action,
      createSubgroup: action,
      renameNamespace: action,
      moveWorkArea: action,
      setSignOutRemovesDeviceData: action,
      keepLogoUpdatedAt: action,
      personal: computed,
    });
  }

  /** The reader's own personal namespace: the one `user` namespace the tree holds. */
  get personal(): INamespace | null {
    return (this.tree ?? []).find((namespace) => namespace.kind === 'user') ?? null;
  }

  /** Every root-level group namespace — an organisation. */
  get organisations(): INamespace[] {
    return (this.tree ?? []).filter(
      (namespace) => namespace.kind === 'group' && !namespace.parentId,
    );
  }

  /** The tree as rows a sidebar renders: every organisation, depth-first, with its subgroups nested. */
  get rows(): INamespaceTreeRow[] {
    const all = this.tree ?? [];
    const childrenOf = (parentId: string): INamespace[] =>
      all
        .filter((namespace) => namespace.kind === 'group' && namespace.parentId === parentId)
        .sort((left, right) => left.path.localeCompare(right.path));

    function build(namespace: INamespace, depth: number): INamespaceTreeRow {
      const children = childrenOf(namespace.id).map((child) => build(child, depth + 1));
      return { namespace, depth, children };
    }

    return this.organisations
      .sort((left, right) => left.path.localeCompare(right.path))
      .map((organisation) => build(organisation, 0));
  }

  async load(): Promise<void> {
    const serial = ++this.requestSerial;
    this.loading = true;
    this.failure = null;
    try {
      const tree = await namespaceApi.tree();
      if (serial !== this.requestSerial) return;
      runInAction(() => {
        this.tree = tree;
        this.loading = false;
      });
      this.keepSignOutRequirement();
    } catch (error) {
      if (serial !== this.requestSerial) return;
      runInAction(() => {
        this.failure = describeLoadFailure(error);
        this.loading = false;
      });
    }
  }

  /**
   * ⛔ Called only by the organisation flow in Global settings (I-0682, AC-O1); a guard reads
   * `web/src` for every other caller.
   */
  async createOrganisation(request: ICreateOrganisationRequest): Promise<INamespace> {
    const created = await namespaceApi.createOrganisation(request);
    runInAction(() => {
      this.tree = [...(this.tree ?? []), created];
    });
    return created;
  }

  async createSubgroup(parentId: string, path: string, name?: string | null): Promise<INamespace> {
    const created = await namespaceApi.createSubgroup(parentId, path, name);
    runInAction(() => {
      this.tree = [...(this.tree ?? []), created];
    });
    return created;
  }

  /**
   * Rename a namespace's handle or name (I-0657, ADR-111), then read the tree again: a handle is
   * the first segment of every descendant's full path, so patching one row would leave its
   * subgroups showing the old address.
   */
  async renameNamespace(
    id: string,
    change: { path?: string | null; name?: string | null },
  ): Promise<INamespace> {
    const renamed = await namespaceApi.rename(id, change);
    await this.load();
    return renamed;
  }

  /** Require, or stop requiring, that signing out removes people's data from the device (I-2092). */
  async setSignOutRemovesDeviceData(id: string, required: boolean): Promise<INamespace> {
    const changed = await namespaceApi.setSignOutRemovesDeviceData(id, required);
    runInAction(() => {
      this.tree = (this.tree ?? []).map((namespace) =>
        namespace.id === changed.id ? changed : namespace,
      );
    });
    this.keepSignOutRequirement();
    return changed;
  }

  /** F-0745: a logo set or removed, as the server answered it, without reading the tree again. */
  keepLogoUpdatedAt(id: string, logoUpdatedAt: string | null): void {
    this.tree = (this.tree ?? []).map((namespace) =>
      namespace.id === id ? { ...namespace, logoUpdatedAt } : namespace,
    );
  }

  /** I-2102: the answer a sign-out that asks nobody falls back on, kept whenever the tree is read. */
  private keepSignOutRequirement(): void {
    cacheSignOutRequired(
      signOutRequirement({ tree: this.tree, loading: false }).state === 'required',
    );
  }

  async moveWorkArea(workAreaId: string, namespaceId: string) {
    return namespaceApi.moveWorkAreaToNamespace(workAreaId, namespaceId);
  }
}
